import { Link } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import type { ClinicRelationship } from '@/entities/business-relationship/api/businessRelationship.api';
import {
  getRelationshipGroup,
  getStatusBadgeTone,
  RELATIONSHIP_GROUP_LABEL,
  type RelationshipGroup,
} from '@/entities/business-relationship/lib/statusView';
import { EmptyState } from '@/shared/components/StateViews';
import { Badge } from '@/shared/components/ui/badge';
import { SearchInput } from '@/shared/components/ui/input';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/utils';
import { formatShortYm } from '@/shared/utils/format';

const GROUP_ORDER: RelationshipGroup[] = ['applying', 'ongoing', 'closed'];

/** 업로드된 처방 내역 박스 — 지난 달 · 이번 달 정산월 장수 (거래중 · 거래 종료) */
const UploadSummary = ({
  item,
  readOnly,
}: {
  item: ClinicRelationship;
  readOnly: boolean;
}) => {
  const rows = [
    { ym: item.lastMonth, count: item.lastMonthPrescriptionCount },
    { ym: item.thisMonth, count: item.thisMonthPrescriptionCount },
  ].filter((r) => !!r.ym);

  return (
    <div className="mt-2.5 flex flex-col gap-[5px] rounded-sm bg-subtle px-2.5 py-2">
      <p
        className={cn(
          'text-[10px] leading-[15px]',
          readOnly ? 'text-[#8a8a8a]' : 'text-ink-7',
        )}
      >
        {readOnly
          ? '거래 종료 전 등록 내역 · 조회만 가능'
          : '업로드된 처방 내역'}
      </p>
      {rows.map((r) => (
        <div
          key={r.ym}
          className="flex items-center text-[11px] leading-[17px]"
        >
          <span className="flex-1 text-ink-4">{formatShortYm(r.ym)} 정산</span>
          <span
            className={cn(
              'font-medium',
              // 0장 강조는 등록을 재촉하는 용도라 거래중에만 쓴다
              !readOnly && r.count === 0 ? 'text-danger' : 'text-ink',
            )}
          >
            {r.count}장
          </span>
        </div>
      ))}
    </div>
  );
};

/**
 * M-04 좌측 마스터 (372px) — 병의원을 고르는 선택 전용 목록.
 * 등록 · 수정 같은 액션 버튼은 두지 않는다 (M-07 §5).
 * 순서는 서버 정렬 그대로. 검색은 받은 목록에서 병의원명으로 거른다 (서버 keyword와 같은 조건)
 */
export const AccountList = ({
  items,
  selectedId,
}: {
  items: ClinicRelationship[];
  selectedId?: number;
}) => {
  const [keyword, setKeyword] = useState('');

  const groups = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    const filtered = q
      ? items.filter((i) => i.clinicName.toLowerCase().includes(q))
      : items;
    return GROUP_ORDER.map((group) => ({
      group,
      items: filtered.filter(
        (i) => getRelationshipGroup(i.statusView) === group,
      ),
    })).filter((g) => g.items.length > 0);
  }, [items, keyword]);

  return (
    <div className="flex flex-col">
      <SearchInput
        icon={<Search className="size-3.5 shrink-0 text-ink" />}
        placeholder="병의원 명으로 검색"
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        aria-label="병의원 명으로 검색"
      />
      {groups.length === 0 && (
        <EmptyState
          message={keyword ? '검색 결과가 없어요.' : '등록된 거래처가 없어요.'}
        />
      )}
      {groups.map(({ group, items: groupItems }) => (
        <section key={group} className="mt-4">
          <div className="flex items-center gap-2">
            <h2 className="text-xs leading-[18px] font-medium text-ink-6">
              {RELATIONSHIP_GROUP_LABEL[group]}
            </h2>
            <Badge>{groupItems.length}</Badge>
          </div>
          <ul className="mt-2.5 flex flex-col gap-2">
            {groupItems.map((item) => {
              const selected = item.relationshipId === selectedId;
              const showBadge =
                group !== 'ongoing' && item.statusView !== '거래 신청';
              const showUploads =
                group === 'ongoing' || item.statusView === '거래 종료';
              return (
                <li key={item.relationshipId}>
                  <Link
                    to={ROUTE_PATHS.ACCOUNT_DETAIL}
                    params={{ relationshipId: String(item.relationshipId) }}
                    aria-current={selected ? 'page' : undefined}
                    className={cn(
                      'block rounded-lg border px-3.5 py-[13px] transition-colors',
                      selected
                        ? 'border-[1.5px] border-primary bg-primary-tint'
                        : 'border-line-soft bg-surface hover:bg-subtle',
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <p className="min-w-0 flex-1 text-[13px] leading-[19px] font-medium text-ink">
                        {item.clinicName}
                      </p>
                      {showBadge && (
                        <Badge tone={getStatusBadgeTone(item.statusView)}>
                          {item.statusView}
                        </Badge>
                      )}
                    </div>
                    {showUploads && (
                      <UploadSummary
                        item={item}
                        readOnly={group === 'closed'}
                      />
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
};
